// Renders a strategy lesson as scannable, typed cards instead of a wall of
// prose. A student alone with the screen sees the task at a glance, ticks off a
// method checklist, and flips through the named traps. Card shapes come from the
// backend parser (content_tools/lesson_cards.py).
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Monitor, Calculator, Target, ListChecks, LayoutTemplate, PenLine,
  AlertTriangle, NotebookPen, Repeat, Clock, BookOpen, Check, ChevronDown,
} from 'lucide-react';

import { cn } from '@/lib/utils';

const TYPE_META = {
  snapshot: { label: "What you'll see", icon: Monitor, tone: 'indigo' },
  scoring: { label: "How it's scored", icon: Calculator, tone: 'amber' },
  tests: { label: 'What it tests', icon: Target, tone: 'sky' },
  method: { label: 'The method', icon: ListChecks, tone: 'indigo' },
  skeleton: { label: 'Reusable template', icon: LayoutTemplate, tone: 'violet' },
  worked_example: { label: 'Worked example', icon: PenLine, tone: 'emerald' },
  traps: { label: 'Traps', icon: AlertTriangle, tone: 'rose' },
  notetaking: { label: 'Note-taking', icon: NotebookPen, tone: 'slate' },
  drills: { label: 'Drills', icon: Repeat, tone: 'emerald' },
  pacing: { label: 'Pacing', icon: Clock, tone: 'slate' },
  concept: { label: 'Concept', icon: BookOpen, tone: 'slate' },
  overview: { label: 'Overview', icon: BookOpen, tone: 'slate' },
};

const TONE = {
  indigo: 'text-indigo-600 bg-indigo-50',
  amber: 'text-amber-600 bg-amber-50',
  sky: 'text-sky-600 bg-sky-50',
  violet: 'text-violet-600 bg-violet-50',
  emerald: 'text-emerald-600 bg-emerald-50',
  rose: 'text-rose-600 bg-rose-50',
  slate: 'text-slate-500 bg-slate-100',
};

const MD = ({ children }) => (
  <div className="card-prose"><ReactMarkdown>{children || ''}</ReactMarkdown></div>
);
const InlineMD = ({ children }) => (
  <ReactMarkdown components={{ p: ({ node, ...p }) => <span {...p} /> }}>{children || ''}</ReactMarkdown>
);

function CardShell({ type, title, kicker, children }) {
  const meta = TYPE_META[type] || TYPE_META.concept;
  const Icon = meta.icon;
  return (
    <section className="border border-slate-200 bg-white" data-testid={`card-${type}`}>
      <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3">
        <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded', TONE[meta.tone])}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{kicker || meta.label}</p>
          <h3 className="font-display text-[15px] font-semibold leading-tight text-slate-900">{title}</h3>
        </div>
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

const Bullets = ({ items }) => (
  <ul className="space-y-2">
    {items.map((b, i) => (
      <li key={i} className="flex gap-2.5 text-[14.5px] leading-6 text-slate-700">
        <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
        <span><InlineMD>{b}</InlineMD></span>
      </li>
    ))}
  </ul>
);

function MethodCard({ card }) {
  const [done, setDone] = useState(() => new Set());
  const toggle = (i) => setDone((prev) => {
    const n = new Set(prev);
    n.has(i) ? n.delete(i) : n.add(i);
    return n;
  });
  return (
    <CardShell type={card.type} title={card.title}
      kicker={card.type === 'method' && card.steps?.length ? `${card.steps.length}-step checklist` : undefined}>
      {card.intro_md && <div className="mb-4"><MD>{card.intro_md}</MD></div>}
      {card.md && <div className="mb-4"><MD>{card.md}</MD></div>}
      {card.slots?.length > 0 && (
        <ol className="mb-1 space-y-1.5">
          {card.slots.map((s, i) => (
            <li key={i} className="flex items-center gap-3 border border-violet-100 bg-violet-50/40 px-3.5 py-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-200 text-[11px] font-bold text-violet-700">{i + 1}</span>
              <span className="text-[14px] font-medium text-slate-700">{s}</span>
            </li>
          ))}
        </ol>
      )}
      {card.steps?.length > 0 && (
        <ol className="space-y-2.5">
          {card.steps.map((s, i) => {
            const checked = done.has(i);
            return (
              <li key={i}>
                <button type="button" onClick={() => toggle(i)} data-testid={`method-step-${i}`}
                  className={cn('flex w-full items-start gap-3 border px-3.5 py-2.5 text-left transition-colors',
                    checked ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50')}>
                  <span className={cn('mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                    checked ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 text-transparent')}>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-[14.5px] font-semibold text-slate-900', checked && 'text-emerald-700 line-through decoration-emerald-400/70')}>{s.label}</span>
                    {s.body_md && <span className="card-prose mt-0.5 block"><ReactMarkdown>{s.body_md}</ReactMarkdown></span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </CardShell>
  );
}

function TrapRow({ trap, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-rose-100" data-testid={`trap-${idx}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-rose-50/40">
        <AlertTriangle className="h-4 w-4 shrink-0 text-rose-500" />
        <span className="flex-1 text-[14.5px] font-semibold text-slate-900">{trap.title}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="border-t border-rose-100 bg-rose-50/30 px-3.5 py-3">
          {trap.body_md && <MD>{trap.body_md}</MD>}
          {trap.example_md && <div className="mt-2"><MD>{trap.example_md}</MD></div>}
          {trap.why && (
            <p className="mt-2.5 text-[13.5px] leading-6 text-rose-800">
              <span className="font-semibold">{trap.why_label || 'Why it works on you'}: </span>{trap.why}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function TrapsCard({ card }) {
  return (
    <CardShell type="traps" title={card.title}
      kicker={`${card.items.length} trap${card.items.length === 1 ? '' : 's'} · click any to expand`}>
      <div className="space-y-2">
        {card.items.map((t, i) => <TrapRow key={i} trap={t} idx={i} />)}
      </div>
    </CardShell>
  );
}

function TextCard({ card }) {
  return (
    <CardShell type={card.type} title={card.title}>
      {card.bullets?.length > 0 && <Bullets items={card.bullets} />}
      {card.md && <div className={cn(card.bullets?.length > 0 && 'mt-3')}><MD>{card.md}</MD></div>}
    </CardShell>
  );
}

export default function LessonCards({ cards }) {
  if (!cards?.length) return null;
  return (
    <div className="space-y-4" data-testid="lesson-cards">
      {cards.map((card, i) => {
        if (card.type === 'method' || card.type === 'skeleton') return <MethodCard key={i} card={card} />;
        if (card.type === 'traps') return <TrapsCard key={i} card={card} />;
        return <TextCard key={i} card={card} />;
      })}
    </div>
  );
}
